import { type CSSProperties, forwardRef, type ReactNode } from "react";

import { cn } from "@btnet/utilities-core";

export interface IBuiSectionProps {
  children?: ReactNode;
  className?: string;
  "data-testid"?: string;
  id?: string;
  style?: CSSProperties;
}

export interface IBuiSectionHeaderProps {
  children?: ReactNode;
  className?: string;
  "data-testid"?: string;
}

/**
 * BuiSection - a presentational container primitive for grouping related content.
 *
 * Renders a padded surface. Compose with `BuiSection.Header` for a header row;
 * anything else is passed as children. Forwards its ref to the underlying `div`.
 * @param props - The component props.
 * @param props.className - Additional CSS class names.
 * @param props["data-testid"] - Test ID for the container.
 * @param props.id - Id applied to the container.
 * @param props.style - Inline styles applied to the container.
 */
const BuiSectionComponent = forwardRef<HTMLDivElement, IBuiSectionProps>(
  (props, ref) => {
    const { className, "data-testid": testid, id, style, children } = props;

    return (
      <div
        ref={ref}
        id={id}
        style={style}
        className={cn(
          "bui-section flex flex-col bg-gray-0 py-2 px-4",
          className,
        )}
        data-testid={testid}
      >
        {children}
      </div>
    );
  },
);

BuiSectionComponent.displayName = "BuiSection";

/**
 * BuiSection.Header - a header row for a `BuiSection`.
 *
 * Lays out its children in a flex row with space between them, suited for a title alongside
 * actions.
 * @param props - The component props.
 * @param props.className - Additional CSS class names.
 * @param props["data-testid"] - Test ID for the header.
 */
const BuiSectionHeader = (props: IBuiSectionHeaderProps) => {
  const { className, "data-testid": testid, children } = props;

  return (
    <div
      className={cn(
        "flex items-center justify-between w-full gap-1",
        className,
      )}
      data-testid={testid}
    >
      {children}
    </div>
  );
};

/**
 * BuiSection - a presentational container primitive for grouping related content.
 *
 * Renders a padded surface. Compose with `BuiSection.Header` for a header row;
 * anything else is passed as children.
 * @param props - The component props.
 * @param props.className - Additional CSS class names.
 * @param props["data-testid"] - Test ID for the container.
 */
export const BuiSection = Object.assign(BuiSectionComponent, {
  /**
   * BuiSection.Header - a header row for a `BuiSection`.
   *
   * Lays out its children in a flex row with space between them, suited for a title alongside
   * actions.
   * @param props - The component props.
   * @param props.className - Additional CSS class names.
   * @param props["data-testid"] - Test ID for the header.
   */
  Header: BuiSectionHeader,
});
